import Button from '@liquidweb-dev/undercurrent/Button';
import CopyButton from '@liquidweb-dev/undercurrent/CopyButton';
import Paper from '@liquidweb-dev/undercurrent/Paper';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import LockIcon from '@liquidweb-dev/undercurrent/icons/lucide/Lock';
import LockOpenIcon from '@liquidweb-dev/undercurrent/icons/lucide/LockOpen';
import { useQueryClient } from '@tanstack/react-query';
import ThirdPartyExcldue from 'components/ThirdpartyExclude';
import FadeCollapse from 'components/FadeCollapse';
import { useLeftHandLayout } from 'components/LeftHandLayout';
import { SECURE_DATA_SHARE_LIST_QUERY_KEY } from 'modules/queries/secureDataShare/useList';
import useSecureDataShareRead from 'modules/queries/secureDataShare/useRead';
import { useDialogSecureNotesContext } from './context';

export interface SecureNoteProps {
	secureDataShareId: number;
}

const SecureNote = ({ secureDataShareId }: SecureNoteProps) => {
	const queryClient = useQueryClient();
	const { screenSize } = useLeftHandLayout();
	const isSmallScreen = screenSize === 'small';

	const { unlockedId, setUnlockedId } = useDialogSecureNotesContext();
	const isLocked = unlockedId !== secureDataShareId;
	const disabled = isLocked && Boolean(unlockedId);

	const {
		mutate: readSecureDataShare,
		data: secureDataShare,
		isLoading: readIsLoading,
	} = useSecureDataShareRead();

	const handleUnlock = () => {
		readSecureDataShare(
			{ id: secureDataShareId },
			{
				onSuccess: () => {
					setUnlockedId(secureDataShareId);
				},
			},
		);
	};

	const handleLock = () => {
		setUnlockedId(null);
		// Invalidate list of secure notes once it's relocked. For notes that
		// only have one view remaining, invalidating upon successful read would
		// remove them from the list
		queryClient.invalidateQueries({
			queryKey: [SECURE_DATA_SHARE_LIST_QUERY_KEY],
		});
	};

	const handleToggleLocked = () => {
		if (isLocked) {
			handleUnlock();

			return;
		}

		handleLock();
	};

	const handleCopy = (
		event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		text: string | null | undefined,
	) => {
		if (!text) {
			// prevent tooltip from showing
			event.preventDefault();

			return;
		}

		navigator.clipboard.writeText(text);
	};

	return (
		<Paper
			elevation={isLocked ? 0 : 2}
			sx={({ borderRadius, vars }) => ({
				borderRadius: borderRadius.rounded,
				transition: 'all 0.2s ease-in-out',
				padding: 150,
				border: '1px solid',
				borderColor: 'transparent',
				display: { xs: 'flex', md: 'block' },
				flexDirection: { xs: 'column', md: 'row' },
				gap: 200,
				alignItems: { xs: 'normal', md: isLocked ? 'normal' : 'center' },
				justifyContent: 'space-between',

				...(isLocked && {
					display: 'flex',
					borderColor: vars.palette.uc.border.weak,
				}),
			})}
		>
			<Stack
				sx={{
					flexShrink: 0,
					flexDirection: 'row',
					gap: 150,
					float: { xs: 'none', md: 'right' },
					paddingLeft: { xs: 0, md: 100 },
					// action buttons need to be fist in DOM tree for correct
					// "float" during "unlocked" state on larger screens.
					order: 1,

					...(isLocked && {
						float: 'none',
						paddingLeft: 0,
						paddingBottom: 0,
					}),
				}}
			>
				{!isLocked && (
					<CopyButton
						onClick={(event) => handleCopy(event, secureDataShare?.secret)}
						iconButtonProps={{ variant: 'barebones', size: 'small' }}
					/>
				)}
				<Tooltip
					title={
						disabled
							? 'You’re currently viewing a Secure Note. Lock it to continue.'
							: false
					}
				>
					<Button
						size={isSmallScreen ? 'medium' : 'small'}
						color="highlight"
						variant={isLocked ? 'primary' : 'secondary'}
						startIcon={isLocked ? <LockOpenIcon /> : <LockIcon />}
						onClick={handleToggleLocked}
						loading={readIsLoading}
						disabled={disabled}
						sx={{ flexShrink: 0, flexGrow: 1 }}
					>
						{isLocked ? 'Unlock note' : 'Lock note'}
					</Button>
				</Tooltip>
			</Stack>

			<Stack sx={{ order: 0 }}>
				<FadeCollapse in={isLocked}>
					<Typography
						fontType="monospace"
						variant="label-lg"
						sx={({ vars }) => ({
							whiteSpace: 'pre-wrap',
							color: vars.palette.uc.text.weak,
						})}
					>
						This note’s content is locked
					</Typography>
				</FadeCollapse>

				<FadeCollapse in={!isLocked}>
					<ThirdPartyExcldue>
						<Typography
							fontType="monospace"
							variant="label-lg"
							component="span"
							sx={{
								whiteSpace: 'pre-wrap',
							}}
						>
							{secureDataShare?.secret}
						</Typography>
					</ThirdPartyExcldue>
				</FadeCollapse>
			</Stack>
		</Paper>
	);
};

export default SecureNote;
